import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip'
import type { ComponentProps, ReactElement, ReactNode } from 'react'
import styles from './styles/tooltip.module.css'
import arrowStyles from './styles/arrow.module.css'

/**
 * Provider del sistema de tooltips. Va una vez, envolviendo la página.
 *
 * Hace dos cosas. La primera es compartir el retraso: una vez que un tooltip se abrió, los
 * siguientes aparecen al instante —Base UI los marca con `data-instant`—, así que recorrer una
 * barra de iconos no se siente lento aunque el primero tarde.
 *
 * La segunda es fijar cuánto tarda ese primero. El default de Base UI son **600 ms**, que sobre una
 * toolbar de iconos se siente roto: llegás, no pasa nada, y te vas. shadcn va al otro extremo y lo
 * pone en **0**, que hace que barrer el mouse por una fila de iconos dispare un destello por cada
 * uno. 300 ms es suficiente para no disparar al pasar de largo y corto para no leerse como demora.
 *
 * **Montarlo es requisito del consumidor, no algo que garantice la capa.** En este repo lo pone el
 * chrome (`@components/shell`, que lo monta en la raíz de cada mockup), pero el chrome no viaja
 * con la capa, así que quien reciba `au/ui` tiene que montar su propio
 * `TooltipProvider` en la raíz de la app. Sin uno, `Tooltip` **funciona** y ahí está el problema:
 * cae al default de 600 ms de Base UI, se siente roto, y ningún gate lo detecta. Anidarlo es inocuo:
 * gana el de adentro.
 */
export function TooltipProvider({ delay = 300, ...props }: ComponentProps<typeof BaseTooltip.Provider>) {
  return <BaseTooltip.Provider delay={delay} {...props} />
}

export interface TooltipProps extends ComponentProps<typeof BaseTooltip.Root> {
  children: ReactElement
  content: ReactNode
  side?: 'top' | 'bottom' | 'left' | 'right'
  /** Flecha apuntando al trigger. **Apagada por default** — ver `PopoverProps.arrow`. En un tooltip
   *  pesa todavía menos: la caja es chica y aparece pegada al control que aclara. */
  arrow?: boolean
}

/**
 * Tooltip para aclarar un control cuyo propósito es otra cosa — el caso típico es un botón de solo
 * icono.
 *
 * **No** sirve para información que haga falta**:** Base UI lo deshabilita en dispositivos táctiles
 * a propósito, porque no hay gesto para revelarlo antes de tocar. Si el dato importa, va inline o
 * en un `Popover`.
 */
export function Tooltip({ children, content, side = 'top', arrow = false, ...props }: TooltipProps) {
  return (
    <BaseTooltip.Root {...props}>
      <BaseTooltip.Trigger render={children} />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner className={styles['au-tooltip-positioner']} side={side} sideOffset={arrow ? 7 : 5}>
          <BaseTooltip.Popup className={styles['au-tooltip']}>
            {arrow && <BaseTooltip.Arrow className={arrowStyles['au-arrow']} />}
            {content}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  )
}
